// packages/components/src/support-ai/ActionPlanPanel.tsx
import React from 'react';
import { MessageOptions } from './MessageOptions';
import { SolutionSelector } from './SolutionSelector';
import { ExecutionPlanDisplay } from './ExecutionPlanDisplay';
import { MessageSquareWarning } from 'lucide-react';
import type { UIMessageOption, SolutionOption, ExecutionPlan } from '@dopomogai/types';

interface ActionPlanPanelProps {
  messageOptions?: UIMessageOption[];
  solutionOptions?: SolutionOption[];
  onMessageSelect: (message: UIMessageOption) => void;
  onSolutionSelect: (option: SolutionOption) => void;
  executionPlan?: ExecutionPlan;
  onPlanApply: () => void;
}

export const ActionPlanPanel: React.FC<ActionPlanPanelProps> = ({
  messageOptions,
  solutionOptions,
  onMessageSelect,
  onSolutionSelect,
  executionPlan,
  onPlanApply,
}) => {
  // --- FIX: The display of options should not depend on the plan's existence. ---
  // They are separate pieces of information that can coexist.
  const hasOptions = (messageOptions && messageOptions.length > 0) || (solutionOptions && solutionOptions.length > 0);
  const hasPlan = !!executionPlan;

  return (
    <div className="space-y-8">
      {/* This block now renders WHENEVER there are options to show. */}
      {hasOptions && (
        <div className="space-y-6">
          <MessageOptions options={messageOptions || []} onSelect={onMessageSelect} />
          <SolutionSelector options={solutionOptions || []} onSelect={onSolutionSelect} />
        </div>
      )}

      {/* This block renders WHENEVER there is a plan to show. */}
      {hasPlan && (
        <ExecutionPlanDisplay plan={executionPlan} onApply={onPlanApply} />
      )}

      {/* This only shows if there is absolutely nothing to do. */}
      {!hasOptions && !hasPlan && (
         <div className="flex flex-col items-center justify-center text-center p-8 bg-slate-100/50 rounded-xl border-2 border-dashed border-slate-300">
            <MessageSquareWarning size={40} className="text-slate-400 mb-4" />
            <h4 className="font-semibold text-slate-700">No Action Plan Available</h4>
            <p className="text-sm text-slate-500 mt-1 max-w-sm">
                The AI has not yet proposed an action plan for this stage of the conversation.
            </p>
        </div>
      )}
    </div>
  );
};